import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Chip } from "@mui/material";
import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  SearchInput,
  Table,
  useDebounce,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import { useCallback, useEffect, useMemo, useState } from "react";
import MainApi from "../../services/mainApi.service";
import { ITicketAutomationTemplate } from "../../models/ticket-automation";
import { resendWhatsappTemplate } from "../../services/whatsappTemplate.service";
import { listAutomations } from "../../services/ticketautomation.service";
import { IGetSitesApps } from "../../models/site-app";
import { IWhatsappTemplateScreenProps } from "./models";

const TICKET_AUTOMATION_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutomationTemplate/";
const ticketAutomationService = new MainApi<ITicketAutomationTemplate>(
  TICKET_AUTOMATION_ROUTE
);

const translationPath = "chatBot.page.templates.whatsapp_template.";

const WhatsappTemplateScreen = ({ software }: IWhatsappTemplateScreenProps) => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(0);

  const { setMessage } = useNotificationContext();

  const [text, setText] = useState<string>(
    () => localStorage.getItem("searchByTemplate") ?? ""
  );

  const searchBy = useDebounce(text, 500);
  const { sendRequest, loading } = useFetch(resendWhatsappTemplate);

  const [automations, setAutomations] = useState<ISelectType[]>([]);
  const [selectedAutomation, setSelectedAutomation] =
    useState<ISelectType | null>(null);

  const { sendRequest: listAutomationsRequest } = useFetch(listAutomations);

  const fetchAutomations = useCallback(async () => {
    const { data, success } = await listAutomationsRequest({
      AllowWhatsappTemplate: true,
      Software: software,
      Enable: true,
    });
    if (data && success) {
      setAutomations(data);

      const savedId = localStorage.getItem("templateSelectedAutomationId");
      if (savedId) {
        const found = data.find((opt) => opt.id === savedId);
        if (found) setSelectedAutomation(found);
      }
    }
  }, [listAutomationsRequest]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  const resendWhatsappTemplateHandler = useCallback(
    async (params: GridRowParams<ITicketAutomationTemplate>) => {
      const { data, success } = await sendRequest({
        item: {
          ticketAutomationTemplateId: params.row.id,
          ticketAutomationTemplateMetasId:
            params.row.ticketAutomationTemplateMetas[
              params.row.ticketAutomationTemplateMetas.length - 1
            ].id,
        },
        id: params.row.id,
      });
      if (data && success) {
        setRefresh((prevState) => prevState + 1);
        setMessage({
          message: t(translationPath + "resend_success"),
          type: "success",
        });
      }
    },
    [sendRequest, setMessage, t]
  );

  const columns: GridColDef<ITicketAutomationTemplate>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "ticketAutomation",
      headerName: t(translationPath + "automation") || "Automation",
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "ticketAutomationTemplateMetas",
      headerName: t(translationPath + "category") || "Category",
      renderCell: (params) => {
        if (!params?.value) return "-";

        const templateJson =
          params?.value.length > 0
            ? JSON.parse(params?.value[params?.value.length - 1]?.content)
            : [];
        return (
          templateJson?.category && (
            <OverflowTooltip
              text={templateJson.category}
              tooltip={templateJson.category}
              textTransform="none"
              variant="body2"
            />
          )
        );
      },
    },
    {
      field: "status",
      headerName:
        t(translationPath + "automation_status") || "Automation Status",
      maxWidth: 200,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.value
              ? t(translationPath + "active")
              : t(translationPath + "inactive")
          }
          color={params.value ? "success" : "error"}
        />
      ),
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IGetSitesApps>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const changeHandler = useCallback((value: ISelectType | null) => {
    setSelectedAutomation(value);
    if (value?.id) {
      localStorage.setItem("templateSelectedAutomationId", value.id);
    } else {
      localStorage.removeItem("templateSelectedAutomationId");
    }
  }, []);

  const changeSearchHandler = useCallback((value: string | null) => {
    const v = (value ?? "").trim();
    setText(v);
    localStorage.setItem("searchByTemplate", v);
  }, []);

  const props = useMemo(() => {
    return {
      refresh: refresh,
      TicketAutomationId: selectedAutomation?.id,
      SearchBy: searchBy.length > 0 ? searchBy : undefined,
      Software: software,
    };
  }, [refresh, searchBy, selectedAutomation, software]);

  return (
    <Table<ITicketAutomationTemplate>
      showDefaultMacroFilters={false}
      service={ticketAutomationService}
      columns={columns}
      title={t(translationPath + "whatsapp_templates")}
      searchInput={false}
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      serviceProps={props}
      macroFilters={() => (
        <>
          <Box
            sx={{
              width: "30%",
              minWidth: "450px",
            }}
          >
            <SearchInput
              onSearch={changeSearchHandler}
              placeholder={t(translationPath + "search")}
              focus={false}
              cleanButton
              defaultValue={text}
              fireOnMount={false}
            />
          </Box>
          <AutoCompleteUncontrolled
            label={t(translationPath + "automation") || "Automation"}
            onValueSelected={changeHandler}
            value={selectedAutomation}
            options={automations}
            clearable={true}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
          />
        </>
      )}
      syncing={loading}
      rowActions={(params) =>
        params.row.status.code === "5"
          ? [
              {
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resend"),
                action: resendWhatsappTemplateHandler,
              },
            ]
          : []
      }
      searchInputAutoFocus
      searchInputPlaceHolder={
        t(translationPath + "search") || "Searh for Whatsapp template"
      }
      addButtonLabel={
        t(translationPath + "add_whatsapp_template") || "Add Whatsapp Template"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default WhatsappTemplateScreen;
